import {useCallback, useEffect, useState} from "react";
import styled from "styled-components/macro";
import {useDropzone} from 'react-dropzone';

import {useCreateImage, useCreateVideo, useUploadToken} from "../api/create_image";
import {useFeedback} from "../store/feedback";
import {extension} from "../utils/files";
import {Spinner} from "@chakra-ui/react";
import {themecolors} from "../utils/themecolors";
import {useSettings} from "../store";

const PreviewImg = styled.img`
  max-height: 200px;
  width: auto;
  object-fit: contain;
  max-width: 100%;
`;

const PreviewVideo = styled.video`
    max-height: 200px;
    max-width: 100%;
`;

const SpinnerContainer = styled.div`
position: absolute;
width: 100%;
`;

const permittedFiletypes = ['jpg', 'jpeg', 'gif', 'png', 'mp4', 'webp'];

const Filetypes = styled.p``;

const ImageField = styled(({className, id, label, onChange, paste, setPaste}) => {
    const {theme} = useSettings();
    const {busy, error, feedbackCh} = useFeedback();
    const upload = useCreateImage(feedbackCh);
    const uploadVideo = useCreateVideo(feedbackCh);
    const uploadToken = useUploadToken(feedbackCh);
    const [rejection, setRejection] = useState('');

    const [preview, setPreview] = useState('');
    const [fileExtensionVal, setFileExtensionVal] = useState(false);

    const [success, setSuccess] = useState(false);

    const handleImage = async (files) =>{

        try {
            const fileExtension = extension(files[0].name).toLowerCase();
            setFileExtensionVal(fileExtension);
            const fileSize = files[0].size / 1024 / 1024;
            if (!permittedFiletypes.includes(fileExtension)) {
                setRejection('That filetype is not accepted, please try again.');
                return;
            }
            if (fileExtension !== 'mp4' && fileExtension !== 'gif' && fileSize > 12) {
                setRejection('Maximum image size is 12MB, please try again.');
                return;
            } else if ((fileExtension === 'mp4' || fileExtension === 'gif') && fileSize > 100) {
                setRejection('Maximum video size is 100MB, please try again.');
                return;
            }

            const previewURL = URL.createObjectURL(files[0]);
            setPreview(previewURL);

            if (fileExtension === 'mp4') {
                const id = await uploadVideo(files[0]);
                onChange(`https://cdn.videy.co/${id}.mp4`);
                setSuccess(true);
            } else {
                const token = await uploadToken('postMedia');
                const id = await upload(files[0], token);
                onChange(`https://media.scored.co/post/${  id}`);
                setSuccess(true);
            }
        } catch (err) {
            // error is displayed
            setPreview(false);
        }

    }

    useEffect(() => {
        if (paste) {
            handleImage([paste]);
            setPaste(false);
        }
    }, [paste]);

    const onDrop = useCallback(async (files) => {
        setRejection('');
        await handleImage(files);
    }, [setPreview, upload, onChange]);
    const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop});

    return (
        <div className={className}>
            <figure {...getRootProps()}>
                <input {...getInputProps()} />
                {isDragActive && <figcaption>Drop the files here</figcaption>}
                {!isDragActive && !busy && !preview.src && !error && !rejection && !success && (
                    <figcaption>
                        <p>Upload an Image or Video</p>
                        <Filetypes>( png, gif, jpg, webp, mp4 )</Filetypes>
                    </figcaption>
                )}
                {(busy || error || rejection || success) &&
                    <figcaption>
                        {preview &&
                        (fileExtensionVal && fileExtensionVal === 'mp4') ? (
                                <PreviewVideo controls={false} autoplay={false} src={preview} />
                            ) : (
                                <PreviewImg src={preview} />
                            )
                        }
                        {busy && <SpinnerContainer>
                            <Spinner
                                thickness='4px'
                                speed='0.65s'
                                emptyColor={theme === "dark" ? themecolors.dark_background_5 : themecolors.light_low_impact_text}
                                color={theme === "dark" ? themecolors.dark_background_2 : "#d4d4d4"}
                                size='lg'
                            />
                        </SpinnerContainer>}
                        {error?.toString()}
                        {rejection &&
                            <>
                                <p>{rejection}</p>
                                <Filetypes>( png, gif, jpg, webp, mp4 )</Filetypes>
                            </>
                        }
                    </figcaption>
                }
            </figure>
        </div>
    )
})`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  figure {
    width: 100%;
    min-height: 200px;
    margin: 0;
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.dark_background_6 : themecolors.pure_white};
    border: ${({theme}) => theme.color.name === 'dark' ? `1px solid rgba(255,255,255,.16)` : '1px solid #e2e8f0'};
    border-radius: 4px;
    align-self: center;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  figcaption {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
  img {
    height: 100%;
    max-height: 200px;
  }
  ${Filetypes} {
    font-size: 14px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
  }
`;

export default ImageField;
